import { API_URL, API_BASE_URL } from '../config/api';
import { mediaUrl, hostDisplayName } from '../constants/qoreTalks';

export function parseTalksResponse(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json.map(normalizeTalk);
  const list = json.data ?? json.talks ?? [];
  return Array.isArray(list) ? list.map(normalizeTalk) : [];
}

export function parseLiveRoomsResponse(json) {
  if (!json) return [];
  const raw = json?.data ?? json?.rooms ?? (Array.isArray(json) ? json : []);
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => ({
    _id: r.roomId ?? r._id,
    roomId: r.roomId ?? r._id,
    title: r.title,
    host: r.host,
    participantCount: r.participantCount ?? 0,
    startedAt: r.startedAt,
    category: r.category,
    isLive: r.isLive ?? r.status === 'live',
    status: r.status,
  }));
}

export function normalizeTalk(talk) {
  if (!talk) return talk;
  return {
    ...talk,
    audioUrl: mediaUrl(talk.audioUrl),
    videoUrl: mediaUrl(talk.videoUrl),
    thumbnailUrl: mediaUrl(talk.thumbnailUrl),
    transcriptUrl: mediaUrl(talk.transcriptUrl),
    host: typeof talk.host === 'object'
      ? { ...talk.host, name: hostDisplayName(talk.host) }
      : { name: talk.host || 'Unknown' },
  };
}

export async function fetchTalks(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_URL}/qoretalks${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch talks');
  return parseTalksResponse(await res.json());
}

export async function fetchTalkById(id) {
  const res = await fetch(`${API_URL}/qoretalks/${id}`);
  if (!res.ok) throw new Error('Episode not found');
  const json = await res.json();
  return normalizeTalk(json.data ?? json);
}

export async function fetchLiveRooms() {
  const res = await fetch(`${API_URL}/qoretalks/live/rooms`);
  if (!res.ok) return [];
  return parseLiveRoomsResponse(await res.json());
}

export async function fetchLiveRoomInfo(roomId, token) {
  const res = await fetch(`${API_URL}/qoretalks/live/meetings/${encodeURIComponent(roomId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  const json = await res.json();
  const d = json.data ?? json;
  return {
    _id: d.roomId ?? d._id ?? roomId,
    roomId: d.roomId ?? d._id ?? roomId,
    title: d.title,
    description: d.description,
    host: d.host,
    participantCount: d.participantCount ?? 0,
    isLive: d.isLive,
    status: d.status,
    startedAt: d.startedAt,
    endedAt: d.endedAt,
    durationSeconds: d.durationSeconds,
    invitePath: d.invitePath || `/qoretalks/live/${d.roomId || roomId}`,
  };
}

export async function createLiveMeeting({ title, description }, token) {
  const res = await fetch(`${API_URL}/qoretalks/live/meetings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ title, description }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to create meeting');
  return json.data;
}

export async function fetchLiveHistory(token, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_URL}/qoretalks/live/history${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to load call history');
  const json = await res.json();
  return json.data || { sessions: [], pagination: {} };
}

export async function createTalk(formData, token) {
  const res = await fetch(`${API_URL}/qoretalks`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to create talk');
  return normalizeTalk(json.data);
}

export async function submitGuestApplication(payload) {
  const res = await fetch(`${API_URL}/qoretalks/guest-applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to submit application');
  return json;
}

export async function subscribeToQoreTalks(payload) {
  const res = await fetch(`${API_URL}/qoretalks/subscribers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to subscribe');
  return json;
}

export { API_BASE_URL };